import { CachedFileItem } from './CachedFileItem';
import { appendArrayBuffer } from './append/appendArrayBuffer';
import { appendFileList } from './append/appendFileList';
import { appendPath } from './append/appendPath';
import { appendSource } from './append/appendSource';
import { appendText } from './append/appendText';
import { appendWebSource } from './append/appendWebSource';
import { fromIum } from './fromIum';
import { toIum } from './toIum';
import { convertExtendedSourceToFile } from './utilities/convertExtendedSourceToFile';
import { expandAndFilter } from './utilities/expand/expandAndFilter';
import { getNameInfo } from './utilities/getNameInfo';
import { shouldAddItem } from './utilities/shouldAddItem';
export class FileCollection {
    files;
    sources;
    options;
    filter;
    cache;
    constructor(options = {}) {
        this.options = options;
        this.filter = options.filter || {};
        this.files = [];
        this.sources = [];
        this.cache = options.cache ?? false;
    }
    /**
     * This is unexpected to be used directly
     * @param source
     * @private
     */
    async appendExtendedSource(source) {
        if (!shouldAddItem(source.relativePath, this.filter))
            return;
        this.sources.push(source);
        const sourceFile = convertExtendedSourceToFile(source);
        const files = await expandAndFilter(sourceFile, this.options);
        for (const file of files) {
            if (this.files.some((existing) => existing.relativePath === file.relativePath)) {
                throw new Error(`Duplicate relativePath: ${file.relativePath}`);
            }
            if (this.cache) {
                this.files.push(new CachedFileItem(file));
            }
            else {
                this.files.push(file);
            }
        }
    }
    /**
     * Save an object to the collection. This method will convert typed array to normal array and will
     * replace potentially existing file with the same name.
     * @param key
     * @param value
     * @returns
     */
    set(key, value) {
        this.removeFile(key);
        const string = JSON.stringify(value, (key, value) => ArrayBuffer.isView(value) ? Array.from(value) : value);
        return this.appendText(key, string);
    }
    removeFile(originalRelativePath) {
        const { relativePath } = getNameInfo(originalRelativePath);
        const index = this.files.findIndex((f) => f.relativePath === relativePath);
        let removedFile;
        if (index !== -1) {
            const sourceUUID = this.files[index].sourceUUID;
            removedFile = this.files.splice(index, 1)[0];
            // any other files with the same sourceUUID?
            if (this.files.some((f) => f.sourceUUID === sourceUUID))
                return;
            // delete the source
            const sourceIndex = this.sources.findIndex((s) => s.uuid === sourceUUID);
            if (sourceIndex !== -1) {
                this.sources.splice(sourceIndex, 1);
            }
            else {
                throw new Error(`Source not found for UUID: ${sourceUUID}`);
            }
        }
        return removedFile;
    }
    async get(key) {
        const { relativePath } = getNameInfo(key);
        const file = this.files.find((f) => f.relativePath === relativePath);
        if (!file) {
            throw new Error(`Key not found: ${key}`);
        }
        const string = await file.text();
        return JSON.parse(string);
    }
    appendWebSource(webSourceURL, options = {}) {
        return appendWebSource(this, webSourceURL, options);
    }
    appendFileList(fileList) {
        return appendFileList(this, fileList);
    }
    appendSource(webSource, options = {}) {
        return appendSource(this, webSource, options);
    }
    /**
     * This method can only be used from nodejs and will throw an error in the browser
     * @param path
     * @param options
     * @param options.keepBasename
     */
    appendPath(path, options = {}) {
        return appendPath(this, path, options);
    }
    appendText(relativePath, text, options = {}) {
        return appendText(this, relativePath, text, options);
    }
    appendArrayBuffer(relativePath, arrayBuffer, options = {}) {
        return appendArrayBuffer(this, relativePath, arrayBuffer, options);
    }
    toIum(options = {}) {
        this.alphabetical();
        return toIum(this, options);
    }
    static async fromIum(ium) {
        const fileCollection = await fromIum(ium);
        fileCollection.alphabetical();
        return fileCollection;
    }
    alphabetical() {
        this.sources.sort((a, b) => a.relativePath < b.relativePath ? -1 : 1);
        this.files.sort((a, b) => a.relativePath < b.relativePath ? -1 : 1);
    }
    [Symbol.iterator]() {
        return this.files.values();
    }
}
//# sourceMappingURL=FileCollection.js.map